import moment from "moment";
import { ApolloError, gql, useMutation } from "@apollo/client";
import {
  GET_EMPLOYEE_CALENDAR,
  GET_EMPLOYEE_TIMESHEETS_AND_ASSIGNMENTS_WEEKLY,
  GET_ABSENCES_INFO,
  useIsMobile,
  GET_EMPLOYEE_TIMESHEETS_AND_ASSIGNMENTS_MONTHLY,
} from "hooks";
import camelCase from "lodash.camelcase";
import mapKeys from "lodash.mapkeys";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { TimeRange, Timesheet } from "types/timesheets";

type UpsertDayOffVars = {
  logType: Timesheet["logType"];
  workLogs: {
    id?: string;
    index: number;
    start_time: Timesheet["startTime"];
    end_time: Timesheet["endTime"];
    description: Timesheet["description"];
    minutes: Timesheet["minutes"];
  }[];
  employeeId: string | null;
};

type UpsertDayOffData = {
  upsertedDayOff: { result: Timesheet[] };
};

const UPSERT_DAY_OFF = gql`
  mutation upsertDayOff($logType: String!, $workLogs: [TimeLogInputType]!, $employeeId: String) {
    upsertedDayOff: submit_time_log(log_type: $logType, work_logs: $workLogs, employee_id: $employeeId) {
      result {
        id
        startTime: start_time
        endTime: end_time
      }
    }
  }
`;

interface Props {
  employeeId: string;
  activeWeek: TimeRange;
  activeMonth: TimeRange;
  activeYear: number;
}

export const useUpsertDayOff = ({
  employeeId,
  activeWeek,
  activeMonth,
  activeYear,
}: Props) => {
  const { start: weekStart, end: weekEnd } = activeWeek;
  const { start: monthStart, end: monthEnd } = activeMonth;

  const { isMobile } = useIsMobile();
  const startDate = moment([activeYear]).startOf("year").format("YYYY-MM-DD");
  const endDate = moment([activeYear]).endOf("year").format("YYYY-MM-DD");

  const [processUpsertDayOff, data] = useMutation<
    UpsertDayOffData,
    UpsertDayOffVars
  >(UPSERT_DAY_OFF, {
    // refetch needed instead of writing to cache (possibly due to returned type)
    refetchQueries: [
      {
        query: GET_EMPLOYEE_TIMESHEETS_AND_ASSIGNMENTS_WEEKLY,
        variables: {
          startDate: weekStart,
          endDate: weekEnd,
          contractStartDate: weekStart,
          contractEndDate: weekStart,
          exceptionsStartDate: weekStart,
          exceptionsEndDate: weekEnd,
          employeeId,
          isManager: false,
        },
      },
      {
        query: GET_EMPLOYEE_CALENDAR,
        variables: { employeeId, startDate, endDate },
      },
      {
        query: GET_ABSENCES_INFO,
      },
      ...(isMobile
        ? []
        : [
            {
              query: GET_EMPLOYEE_TIMESHEETS_AND_ASSIGNMENTS_MONTHLY,
              variables: {
                startDate: monthStart,
                endDate: monthEnd,
                contractStartDate: monthStart,
                contractEndDate: monthStart,
                exceptionsStartDate: monthStart,
                exceptionsEndDate: monthEnd,
                employeeId,
                isManager: false,
              },
            },
          ]),
    ],
  });

  const upsertDayOff = useCallback(
    async (vars: UpsertDayOffVars) => {
      try {
        await processUpsertDayOff({ variables: vars });
      } catch (ex) {
        let errors = undefined;
        const message = (ex as ApolloError).message.replaceAll(`'`, `"`);

        try {
          errors = JSON.parse(message);
          if (Array.isArray(errors)) {
            errors = errors.map((e) => mapKeys(e, (_, key) => camelCase(key)));
          } else {
            toast.error(message);
          }
        } catch (ex) {
          toast.error(message);
          errors = message;
        }
        return { errors };
      }
    },
    [processUpsertDayOff]
  );

  return { upsertDayOff, ...data };
};
